/**
 * MODULE: _shared/frontend-shell/src/components/assistant/AssistantProactiveAdvisorWelcome.tsx
 * PURPOSE: New-session welcome when brain has risk alerts, session deltas, or learned prefs [2.2.1225].
 * PUBLIC_API: AssistantProactiveAdvisorWelcome
 * RECENT: 2026-07-08 [2.2.1478] — Learning status banner removed; top-nav BrainLearningsNavBadge owns learnings.
 * RECENT_PRIOR: 2026-07-08 [2.2.1478] — Risk alert cards removed; surfaced in top-nav BrainAlertsNavBadge only.
 * RECENT_PRIOR: 2026-07-03 [2.2.1477] — Compact page landing: omit duplicate learning banner and prompt chips (composer dock hosts starters).
 * RECENT_PRIOR: 2026-06-06 [2.2.1328] — above-fold email action chips; hide duplicates when dock brain rail hosts context actions (`contextActionChipsHostedExternally`).
 * RECENT_PRIOR: 2026-05-26 [2.2.1254] — action chips route through onContextAction (compose_draft CRM email).
 */

import { Badge, Button } from 'flowbite-react';
import { Brain, Mail, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import type {
  AssistantContextAction,
  AssistantContextResponse,
} from '@shared/lib/frontend/api/assistantContextApi';
import { brainLearnedPreferencesSettingsPath } from '@shared/lib/frontend/utils/brainInsightLinks';
import { formatLearnedPreferenceExplanation } from '../brain/brainLearnedPreferenceExplainUtils';
import { AssistantContextSessionDelta } from './AssistantContextSessionDelta';
import { AssistantContextHarnessNote } from './AssistantContextSelfLearningSections';
import {
  collectProactiveAdvisorPrompts,
  collectProactiveAdvisorContextActions,
  filterPromptsExcludingContextActions,
  hasProactiveAdvisorWelcome,
  proactiveWelcomeDescription,
  proactiveWelcomeHeadline,
  proactiveWelcomeMode,
} from './assistantProactiveAdvisorUtils';
import { EmailTemplateActionBadges } from './EmailTemplateActionBadges';
import { getEmailTemplateActionDisplay } from './emailTemplateActionDisplay';

export type AssistantProactiveAdvisorWelcomeProps = {
  context: AssistantContextResponse | null;
  loading?: boolean;
  onSuggestedPrompt?: (prompt: string) => void;
  /** Routes draft/summarize chips (compose_draft navigates to CRM email compose). */
  onContextAction?: (action: AssistantContextAction) => void;
  onViewContext?: () => void;
  compact?: boolean;
  /** When true (⌘K dock brain rail), omit duplicate Send/List/Create action chips. */
  contextActionChipsHostedExternally?: boolean;
};

export function AssistantProactiveAdvisorWelcome({
  context,
  loading = false,
  onSuggestedPrompt,
  onContextAction,
  onViewContext,
  compact = false,
  contextActionChipsHostedExternally = false,
}: AssistantProactiveAdvisorWelcomeProps) {
  if (loading || !hasProactiveAdvisorWelcome(context)) return null;

  const mode = proactiveWelcomeMode(context);
  const sessionChanges = context?.session_delta?.has_changes
    ? (context.session_delta.changes ?? []).slice(0, 2)
    : [];
  const learnedPreferences = (context?.learned_preferences ?? []).slice(0, 3);
  const pipelineEligible = context?.promotion_pipeline?.eligible_candidates ?? 0;
  const pipelineAlmostReady = context?.promotion_pipeline?.almost_ready_candidates ?? 0;
  const pipelinePending = context?.promotion_pipeline?.pending_candidates ?? 0;
  const pipelineGuidance = (context?.promotion_pipeline?.learning_guidance || '').trim();
  const showLearningPipelineStats =
    pipelinePending > 0 || pipelineAlmostReady > 0 || pipelineEligible > 0 || Boolean(pipelineGuidance);
  const actionChips = collectProactiveAdvisorContextActions(context);
  const promptChips = filterPromptsExcludingContextActions(
    collectProactiveAdvisorPrompts(context),
    actionChips,
  );
  const isLearnedMode = mode === 'learned';
  const isLearningMode = mode === 'learning';
  const isEmailTemplatesMode = mode === 'email_templates';
  const useVioletTheme = isLearnedMode || isLearningMode || isEmailTemplatesMode;
  const showContextActionChips = actionChips.length > 0 && !contextActionChipsHostedExternally;
  const pinEmailActionsAboveFold = isEmailTemplatesMode && showContextActionChips;
  const welcomeDescription = proactiveWelcomeDescription(context);

  const renderActionChips = () =>
    showContextActionChips && onContextAction
      ? actionChips.map((action) => {
          const isCompose = (action.execution_mode || '').toLowerCase() === 'compose_draft';
          const isExecute = (action.execution_mode || '').toLowerCase() === 'advisor_execute';
          const isCreateTpl =
            (action.execution_mode || '').toLowerCase() === 'open_email_template_create';
          const label = (action.label || '').trim();
          const display = getEmailTemplateActionDisplay(action);
          return (
            <Button
              key={label}
              size="sm"
              color="indigo"
              outline
              data-testid={
                isCreateTpl
                  ? 'assistant-proactive-advisor-create-email-template-chip'
                  : isCompose
                    ? 'assistant-proactive-advisor-compose-chip'
                    : 'assistant-proactive-advisor-action-chip'
              }
              data-execution-mode={action.execution_mode || undefined}
              onClick={() => onContextAction(action)}
            >
              {isCompose || isExecute || isCreateTpl ? (
                <span className="inline-flex flex-wrap items-center gap-1">
                  {isCompose || isCreateTpl ? (
                    <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  )}
                  <span>{display.displayLabel}</span>
                  {display.isEmailSnapshot ? (
                    <EmailTemplateActionBadges
                      recommended={display.recommended}
                      uses={display.uses}
                      tone="indigo"
                    />
                  ) : null}
                </span>
              ) : (
                display.displayLabel
              )}
            </Button>
          );
        })
      : null;

  return (
    <div
      className={`flex flex-col items-center text-center ${compact ? 'py-4' : 'py-12'}`}
      data-testid="assistant-proactive-advisor-welcome"
      data-proactive-mode={mode ?? undefined}
    >
      <div
        className={`mb-4 flex items-center justify-center rounded-full ${
          useVioletTheme
            ? 'bg-violet-100 dark:bg-violet-950/50'
            : 'bg-rose-100 dark:bg-rose-950/50'
        } ${compact ? 'h-12 w-12' : 'h-16 w-16'}`}
      >
        {isEmailTemplatesMode ? (
          <Mail
            className={`text-violet-600 dark:text-violet-400 ${compact ? 'h-6 w-6' : 'h-8 w-8'}`}
            aria-hidden
          />
        ) : useVioletTheme ? (
          <Brain
            className={`text-violet-600 dark:text-violet-400 ${compact ? 'h-6 w-6' : 'h-8 w-8'}`}
            aria-hidden
          />
        ) : (
          <Sparkles
            className={`text-primary-600 dark:text-primary-400 ${compact ? 'h-6 w-6' : 'h-8 w-8'}`}
            aria-hidden
          />
        )}
      </div>
      <h2
        className={`font-semibold text-gray-900 dark:text-white ${compact ? 'text-lg' : 'text-xl'} mb-2`}
      >
        {proactiveWelcomeHeadline(context)}
      </h2>
      {welcomeDescription ? (
        <p className="mb-4 max-w-md text-sm text-gray-500 dark:text-gray-400">{welcomeDescription}</p>
      ) : null}

      {pinEmailActionsAboveFold ? (
        <div
          className="mb-6 flex flex-wrap items-center justify-center gap-2"
          data-testid="assistant-proactive-advisor-primary-actions"
        >
          {renderActionChips()}
        </div>
      ) : null}

      {sessionChanges.length > 0 ? (
        <AssistantContextSessionDelta changes={sessionChanges} inline maxItems={2} />
      ) : null}

      {learnedPreferences.length > 0 &&
      !isEmailTemplatesMode &&
      sessionChanges.length === 0 ? (
        <ul
          className="mb-4 w-full max-w-lg space-y-2 text-left"
          data-testid="assistant-proactive-advisor-learned"
        >
          {learnedPreferences.map((pref, idx) => (
            <li
              key={`${pref.label}-${idx}`}
              className="rounded-lg border border-violet-200 bg-violet-50/80 p-3 dark:border-violet-900/50 dark:bg-violet-950/30"
            >
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <Badge color="purple" size="xs">
                  Learned
                </Badge>
              </div>
              <p className="text-sm font-medium text-violet-950 dark:text-violet-100">
                {pref.label?.trim() || 'Preference'}
              </p>
              <p
                className="mt-1 text-xs leading-relaxed text-violet-800/90 dark:text-violet-200/90"
                data-testid="assistant-proactive-advisor-learned-explanation"
              >
                {formatLearnedPreferenceExplanation({
                  memory_key: pref.memory_key,
                  label: pref.label,
                  confidence: pref.confidence,
                  recurrence_count: pref.recurrence_count,
                })}
              </p>
            </li>
          ))}
        </ul>
      ) : null}

      {isLearningMode && sessionChanges.length === 0 && showLearningPipelineStats ? (
        <div
          className="mb-4 w-full max-w-lg rounded-lg border border-violet-200 bg-violet-50/80 p-3 text-left dark:border-violet-900/50 dark:bg-violet-950/30"
          data-testid="assistant-proactive-advisor-learning"
        >
          <ul className="space-y-1 text-sm text-violet-950 dark:text-violet-100">
            {pipelinePending > 0 ? (
              <li>
                <span className="font-medium">{pipelinePending}</span> pattern
                {pipelinePending === 1 ? '' : 's'} being watched
              </li>
            ) : null}
            {pipelineAlmostReady > 0 ? (
              <li>
                <span className="font-medium">{pipelineAlmostReady}</span> almost ready to promote
              </li>
            ) : null}
            {pipelineEligible > 0 ? (
              <li>
                <span className="font-medium">{pipelineEligible}</span> ready to promote now
              </li>
            ) : null}
          </ul>
          {pipelineGuidance ? (
            <p
              className="mt-2 text-xs text-violet-900/90 dark:text-violet-100/90"
              data-testid="assistant-proactive-advisor-learning-guidance"
            >
              {pipelineGuidance}
            </p>
          ) : null}
          <Link
            to={brainLearnedPreferencesSettingsPath()}
            className="mt-2 inline-block text-[11px] font-medium text-violet-700 underline decoration-violet-400/60 underline-offset-2 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-100"
            data-testid="assistant-proactive-advisor-settings-link"
          >
            View learning status in Settings
          </Link>
        </div>
      ) : null}

      {!compact && !isEmailTemplatesMode ? (
        <div className="mx-auto mb-4 w-full max-w-lg">
          <AssistantContextHarnessNote harness={context?.context_harness} />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-center gap-2">
        {!pinEmailActionsAboveFold ? renderActionChips() : null}
        {!compact && promptChips.length > 0 && onSuggestedPrompt
          ? promptChips.map((prompt) => (
              <Button
                key={prompt}
                size="sm"
                color={useVioletTheme ? 'purple' : 'failure'}
                outline={pinEmailActionsAboveFold}
                data-testid="assistant-proactive-advisor-prompt-chip"
                onClick={() => onSuggestedPrompt(prompt)}
              >
                {prompt}
              </Button>
            ))
          : null}
        {onViewContext ? (
          <Button
            size="sm"
            color="light"
            data-testid="assistant-proactive-advisor-view-context"
            onClick={onViewContext}
          >
            View context
          </Button>
        ) : null}
      </div>

      {!compact ? (
        <p className="mt-6 flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          Or start with any question below
        </p>
      ) : null}
    </div>
  );
}
